customElements.define(
  "x-slider",
  class extends HTMLElement {
    get arrows() {
      return this.hasAttribute("data-arrows");
    }

    get progress() {
      return this.hasAttribute("data-progress");
    }

    get dots() {
      return this.hasAttribute("data-dots");
    }

    get loop() {
      return this.hasAttribute("data-loop");
    }

    get align() {
      return this.getAttribute("data-align") ?? "start";
    }

    connectedCallback() {
      const breakpoint = this.getAttribute("data-breakpoint");
      if (breakpoint) {
        const mm = matchMedia(breakpoint);
        mm.matches && this.initEmbla();
        mm.addListener((e) => {
          e.matches ? this.initEmbla() : this.deinitEmbla();
        });
      } else {
        this.initEmbla();
      }
    }

    disconnectedCallback() {
      this.deinitEmbla();
    }

    deinitEmbla() {
      if (this.embla) {
        this.embla.destroy();
        this.classList.remove("is-carousel");
      }
    }

    initEmbla() {
      this.embla = EmblaCarousel(
        this,
        {
          container: ".js-embla-container",
          inViewThreshold: 1,
          loop: this.loop,
          align: this.align,
          skipSnaps: true,
        },
        [this._pluginClassNames()()],
      );

      if (this.progress) this.renderProgress();
      if (this.arrows) this.renderArrows();
      if (this.dots) this.renderDots();

      this.classList.add("is-carousel");
    }

    renderArrows() {
      const arrowsNode = document.createElement("div");
      arrowsNode.className = "embla-buttons";

      const elLeft = document.createElement("button");
      elLeft.type = "button";
      elLeft.className = "embla-button slider-button--prev";
      elLeft.setAttribute("aria-label", "Previous");
      elLeft.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.embla.scrollPrev();
      });
      elLeft.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
      });

      const elRight = document.createElement("button");
      elRight.type = "button";
      elRight.className = "embla-button slider-button--next";
      elRight.setAttribute("aria-label", "Next");
      elRight.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (this.embla.scrollSnapList()[this.embla.selectedScrollSnap()] === 1)
          return;
        this.embla.scrollNext();
      });
      elRight.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
      });

      elLeft.innerHTML = elRight.innerHTML = `
        <span></span>
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="14" fill="none" viewBox="0 0 13 14"><path fill="#FCE4E5" d="M7.74 11.848 5.868 13.18a23.78 23.78 0 0 0-1.692-2.502 16.393 16.393 0 0 0-1.728-1.962 14.994 14.994 0 0 0-1.89-1.602v-.108a17.305 17.305 0 0 0 1.89-1.602c.6-.576 1.176-1.23 1.728-1.962A25.479 25.479 0 0 0 5.868.922l1.872 1.35a15.885 15.885 0 0 1-1.566 2.034A14.12 14.12 0 0 1 4.554 5.8c-.54.42-1.068.762-1.584 1.026a7.06 7.06 0 0 1-1.458.54v-.612a7.06 7.06 0 0 1 1.458.54 9.126 9.126 0 0 1 1.584 1.008c.54.42 1.08.924 1.62 1.512.54.576 1.062 1.254 1.566 2.034Zm4.806-3.564H7.524c-.9 0-1.734-.042-2.502-.126a23.68 23.68 0 0 1-2.106-.324l-1.35-.774 1.35-.792a23.68 23.68 0 0 1 2.106-.324 23.09 23.09 0 0 1 2.502-.126h5.022v2.466Z"/></svg>
      `;

      arrowsNode.append(elLeft, elRight);

      // State
      if (!this.embla.canScrollNext() && !this.embla.canScrollPrev()) {
        arrowsNode.classList.add("invisible");
      } else {
        arrowsNode.classList.remove("invisible");
      }

      const togglePrevNextBtnsState = () => {
        if (this.embla.canScrollPrev()) elLeft.removeAttribute("disabled");
        else elLeft.setAttribute("disabled", "disabled");

        if (this.embla.canScrollNext()) elRight.removeAttribute("disabled");
        else elRight.setAttribute("disabled", "disabled");
      };

      const toggleArrowsState = () => {
        if (!this.embla.canScrollNext() && !this.embla.canScrollPrev()) {
          arrowsNode.classList.add("invisible");
        } else {
          arrowsNode.classList.remove("invisible");
        }
      };

      const removeArrows = () => {
        arrowsNode.remove();
      };

      this.embla
        .on("select", togglePrevNextBtnsState)
        .on("init", togglePrevNextBtnsState)
        .on("reInit", togglePrevNextBtnsState)
        .on("resize", toggleArrowsState)
        .on("destroy", removeArrows);

      this.append(arrowsNode);
    }

    renderProgress() {
      let progressNode = this.querySelector(
        ".ob-custom-progress-bar-container, .js-embla-progress",
      );
      if (!progressNode) {
        // TODO: Create node
        return;
      }

      let min = parseFloat(progressNode.firstElementChild.style.width || "0%");
      if (progressNode.dataset.min) {
        min = parseFloat(progressNode.dataset.min);
      }

      const applyProgress = () => {
        const progress = Math.max(0, Math.min(1, this.embla.scrollProgress()));
        progressNode.style.setProperty(
          "--progress",
          Math.max(min, progress * 100),
        );
      };

      const removeProgress = () => {
        progressNode.style.removeProperty("--progress");
      };

      this.embla
        .on("init", applyProgress)
        .on("reInit", applyProgress)
        .on("scroll", applyProgress)
        .on("slideFocus", applyProgress)
        .on("destroy", removeProgress);
    }

    renderDots() {
      const dotsNode = document.createElement("div");
      dotsNode.className = "embla__dots";

      let dotNodes = [];

      const addDotBtnsWithClickHandlers = () => {
        dotsNode.innerHTML = this.embla
          .scrollSnapList()
          .map(() => '<button class="embla__dot" type="button">•</button>')
          .join("");

        const scrollTo = (index) => {
          this.embla.scrollTo(index);
        };

        dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));
        dotNodes.forEach((dotNode, index) => {
          dotNode.addEventListener("click", () => scrollTo(index), false);
        });
      };

      const toggleDotBtnsActive = () => {
        const previous = this.embla.previousScrollSnap();
        const selected = this.embla.selectedScrollSnap();
        dotNodes[previous].classList.remove("embla__dot--selected");
        dotNodes[selected].classList.add("embla__dot--selected");
      };

      const removeDots = () => {
        dotsNode.remove();
      };

      this.embla
        .on("init", addDotBtnsWithClickHandlers)
        .on("reInit", addDotBtnsWithClickHandlers)
        .on("init", toggleDotBtnsActive)
        .on("reInit", toggleDotBtnsActive)
        .on("select", toggleDotBtnsActive)
        .on("destroy", removeDots);

      this.append(dotsNode);
    }

    _pluginClassNames() {
      const defaultOptions = {
        active: true,
        breakpoints: {},
        snapped: "is-snapped",
        inView: "is-in-view",
        draggable: "is-draggable",
        dragging: "is-dragging",
        loop: "is-loop",
      };

      function normalizeClassNames(classNames) {
        const normalized = Array.isArray(classNames)
          ? classNames
          : [classNames];
        return normalized.filter(Boolean);
      }

      function removeClass(node, classNames) {
        if (!node || !classNames.length) return;
        node.classList.remove(...classNames);
      }

      function addClass(node, classNames) {
        if (!node || !classNames.length) return;
        node.classList.add(...classNames);
      }

      function ClassNames(userOptions = {}) {
        let options;
        let emblaApi;
        let root;
        let slides;
        let snappedIndexes = [];
        let inViewIndexes = [];

        const selectedEvents = ["select"];
        const draggingEvents = ["pointerDown", "pointerUp"];
        const inViewEvents = ["slidesInView"];
        const classNames = {
          snapped: [],
          inView: [],
          draggable: [],
          dragging: [],
          loop: [],
        };

        function init(emblaApiInstance, optionsHandler) {
          emblaApi = emblaApiInstance;

          const { mergeOptions, optionsAtMedia } = optionsHandler;
          const optionsBase = mergeOptions(
            defaultOptions,
            ClassNames.globalOptions,
          );
          const allOptions = mergeOptions(optionsBase, userOptions);
          options = optionsAtMedia(allOptions);

          root = emblaApi.rootNode();
          slides = emblaApi.slideNodes();

          const { watchDrag, loop } = emblaApi.internalEngine().options;
          const isDraggable = !!watchDrag;

          if (options.loop && loop) {
            classNames.loop = normalizeClassNames(options.loop);
            addClass(root, classNames.loop);
          }

          if (options.draggable && isDraggable) {
            classNames.draggable = normalizeClassNames(options.draggable);
            addClass(root, classNames.draggable);
          }

          if (options.dragging) {
            classNames.dragging = normalizeClassNames(options.dragging);
            draggingEvents.forEach((evt) =>
              emblaApi.on(evt, toggleDraggingClass),
            );
          }

          if (options.snapped) {
            classNames.snapped = normalizeClassNames(options.snapped);
            selectedEvents.forEach((evt) =>
              emblaApi.on(evt, toggleSnappedClasses),
            );
            toggleSnappedClasses();
          }

          if (options.inView) {
            classNames.inView = normalizeClassNames(options.inView);
            inViewEvents.forEach((evt) =>
              emblaApi.on(evt, toggleInViewClasses),
            );
            toggleInViewClasses();
          }
        }

        function destroy() {
          draggingEvents.forEach((evt) =>
            emblaApi.off(evt, toggleDraggingClass),
          );
          selectedEvents.forEach((evt) =>
            emblaApi.off(evt, toggleSnappedClasses),
          );
          inViewEvents.forEach((evt) => emblaApi.off(evt, toggleInViewClasses));

          removeClass(root, classNames.loop);
          removeClass(root, classNames.draggable);
          removeClass(root, classNames.dragging);
          toggleSlideClasses([], snappedIndexes, classNames.snapped);
          toggleSlideClasses([], inViewIndexes, classNames.inView);

          Object.keys(classNames).forEach((classNameKey) => {
            classNames[classNameKey] = [];
          });
        }

        function toggleDraggingClass(_, evt) {
          const toggleClass = evt === "pointerDown" ? addClass : removeClass;
          toggleClass(root, classNames.dragging);
        }

        function toggleSlideClasses(
          addClassIndexes = [],
          removeClassIndexes = [],
          classNames,
        ) {
          const removeClassSlides = removeClassIndexes.map(
            (index) => slides[index],
          );
          const addClassSlides = addClassIndexes.map((index) => slides[index]);

          removeClassSlides.forEach((slide) => removeClass(slide, classNames));
          addClassSlides.forEach((slide) => addClass(slide, classNames));

          return addClassIndexes;
        }

        function toggleSnappedClasses() {
          const { slideRegistry } = emblaApi.internalEngine();
          const newSnappedIndexes =
            slideRegistry[emblaApi.selectedScrollSnap()];

          snappedIndexes = toggleSlideClasses(
            newSnappedIndexes,
            snappedIndexes,
            classNames.snapped,
          );
        }

        function toggleInViewClasses() {
          const newInViewIndexes = emblaApi.slidesInView();

          inViewIndexes = toggleSlideClasses(
            newInViewIndexes,
            inViewIndexes,
            classNames.inView,
          );
        }

        const self = {
          name: "classNames",
          options: userOptions,
          init,
          destroy,
        };
        return self;
      }

      ClassNames.globalOptions = undefined;

      return ClassNames;
    }
  },
);
